import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Experiment,
  GetUserMetadataResponse,
  UserPreferenceKeyEnum,
} from "redmond";
import {
  ActiveExperiments,
  AVAILABLE,
  getExperimentVariant,
} from "../../../context/experiments";
import { trackEvent } from "../../../api/v1/analytics/trackEvent";
import { userApiPrefix } from "../../../api/v1/paths";

const SURVEY_ID = "my_trips_csat";
const SURVEY_VERSION = 1;
const SURVEY_SOURCE = "legacy_my_trips";
const USER_METADATA_PATH = `${userApiPrefix}/preference/metadata`;
const SURVEY_METADATA_KEY = UserPreferenceKeyEnum.hasSeenMyTripsCsatSurveyV1;
const CSAT_SHOWN = "csat_shown";
const CSAT_RESPONSE = "csat_response";
const CSAT_DISMISSED = "csat_dismissed";

let hasShownInSession = false;

interface UseMyTripsCsatSurveyProps {
  areTripsLoading: boolean | null;
  experiments: Experiment[];
  hasBookedTrip: boolean;
  hasOpenTripsModal: boolean;
  isMobile: boolean;
  isTravelAppTripDetailsPage: boolean;
  path: string;
  totalTripsCount: number;
}

interface CsatSurveyResponse {
  question: string;
  questionId: string;
  responseValue: number;
  step: number;
}

export const useMyTripsCsatSurvey = ({
  areTripsLoading,
  experiments,
  hasBookedTrip,
  hasOpenTripsModal,
  isMobile,
  isTravelAppTripDetailsPage,
  path,
  totalTripsCount,
}: UseMyTripsCsatSurveyProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSeenSurvey, setHasSeenSurvey] = useState<boolean | null>(null);
  const hasPersistedRef = useRef(false);

  const isFlagEnabled =
    getExperimentVariant(experiments, ActiveExperiments.MY_TRIPS_CSAT) ===
    AVAILABLE;

  useEffect(() => {
    setHasSeenSurvey(null);

    if (!isFlagEnabled || isMobile || hasShownInSession) {
      setIsOpen(false);
      return;
    }

    let isCancelled = false;

    axios
      .post<GetUserMetadataResponse>(USER_METADATA_PATH, {
        keys: [SURVEY_METADATA_KEY],
      })
      .then((response) => {
        if (isCancelled) {
          return;
        }

        setHasSeenSurvey(
          response.data.userMetadata?.hasSeenMyTripsCsatSurveyV1 === true,
        );
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setHasSeenSurvey(null);
      });

    return () => {
      isCancelled = true;
    };
  }, [isFlagEnabled, isMobile]);

  useEffect(() => {
    const shouldShow =
      isFlagEnabled &&
      hasSeenSurvey === false &&
      areTripsLoading === false &&
      hasBookedTrip &&
      !hasOpenTripsModal &&
      !isMobile &&
      !isTravelAppTripDetailsPage &&
      !hasShownInSession;

    if (!shouldShow) {
      return;
    }

    hasShownInSession = true;
    setIsOpen(true);
    trackEvent({
      eventName: CSAT_SHOWN,
      properties: {
        source: SURVEY_SOURCE,
        path,
        survey_id: SURVEY_ID,
        survey_version: SURVEY_VERSION,
        total_trips_count: totalTripsCount,
      },
    }).catch(() => undefined);
  }, [
    areTripsLoading,
    hasBookedTrip,
    hasOpenTripsModal,
    hasSeenSurvey,
    isFlagEnabled,
    isMobile,
    isTravelAppTripDetailsPage,
    path,
    totalTripsCount,
  ]);

  const persistSeenMetadata = useCallback(async () => {
    if (hasPersistedRef.current) {
      return;
    }
    hasPersistedRef.current = true;
    setHasSeenSurvey(true);

    try {
      await axios.put(USER_METADATA_PATH, {
        userMetadata: {
          hasSeenMyTripsCsatSurveyV1: true,
        },
      });
    } catch {
      // Keep the in-session guard even if persistence fails.
    }
  }, []);

  const dismissSurvey = useCallback(
    (step: number) => {
      setIsOpen(false);

      persistSeenMetadata().catch(() => undefined);

      trackEvent({
        eventName: CSAT_DISMISSED,
        properties: {
          source: SURVEY_SOURCE,
          path,
          survey_id: SURVEY_ID,
          survey_version: SURVEY_VERSION,
          total_trips_count: totalTripsCount,
          step,
        },
      }).catch(() => undefined);
    },
    [path, persistSeenMetadata, totalTripsCount],
  );

  const trackResponse = useCallback(
    async ({
      question,
      questionId,
      responseValue,
      step,
    }: CsatSurveyResponse) => {
      if (step === 1) {
        persistSeenMetadata().catch(() => undefined);
      }

      trackEvent({
        eventName: CSAT_RESPONSE,
        properties: {
          source: SURVEY_SOURCE,
          path,
          survey_id: SURVEY_ID,
          survey_version: SURVEY_VERSION,
          total_trips_count: totalTripsCount,
          response_value: responseValue,
          question_id: questionId,
          question,
          step,
        },
      }).catch(() => undefined);
    },
    [path, persistSeenMetadata, totalTripsCount],
  );

  const completeSurvey = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    onComplete: completeSurvey,
    onDismiss: dismissSurvey,
    onResponse: trackResponse,
  };
};
